import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Users, FileText, AlertCircle, Truck, ShoppingCart, FolderKanban, FileSpreadsheet, UserCircle, CreditCard, UserCog, X, Eye, Edit, Wallet, ClipboardList, Download, Package, Receipt } from 'lucide-react';
import { useCan, ActionGatedButton } from '../components/ActionGate';

const Approvals = () => {
  const [pendingData, setPendingData] = useState({
    customers: [], suppliers: [], quotations: [], invoices: [],
    purchase_orders: [], projects: [], payments: [], employees: [],
    payroll: [], employee_loans: [], expense_reports: [], inventory: [],
    supplier_bills: [], sales_quotations: [],
    total_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('customers');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [filePreview, setFilePreview] = useState(null); // For file preview modal
  // For supplier-bills: list of Purchase Orders so the user can link an
  // existing PO (preferred) or fall back to a free-text PO Number.
  const [allPurchaseOrders, setAllPurchaseOrders] = useState([]);
  // Toggle between PO dropdown and free-text input inside the bill editor.
  const [poManualMode, setPoManualMode] = useState(false);

  useEffect(() => {
    fetchPendingApprovals();
    // POs are needed for the Supplier Bill editor's PO dropdown. Cheap
    // enough to fetch alongside the approvals payload.
    axiosInstance.get('/purchase-orders')
      .then((r) => setAllPurchaseOrders(r.data || []))
      .catch(() => setAllPurchaseOrders([]));
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchPendingApprovals();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      const response = await axiosInstance.get('/approvals/pending');
      setPendingData(response.data);
    } catch (error) {
      // Silent fail for auto-refresh, only show error on initial load
      if (loading) {
        toast.error('Failed to fetch pending approvals');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (type, id) => {
    try {
      await axiosInstance.post(`/approvals/${type}/${id}/approve`);
      toast.success('Approved successfully!');
      setSelectedItem(null);
      setEditMode(false);
      fetchPendingApprovals();
    } catch (error) {
      const raw = error.response?.data?.detail || 'Failed to approve';
      const msg = typeof raw === 'string' ? raw : (raw.message || JSON.stringify(raw));
      // Feb 2026 · One-click repair for PAYMENT_MISSING_BANK.
      // The GL guard refuses to post a bank-method payment without a
      // ``bank_account_id``. Instead of forcing the operator to cancel
      // and recreate, offer an inline auto-attach.
      if (type === 'payments' && /PAYMENT_MISSING_BANK|payment_method.*bank_transfer.*bank_account_id/i.test(msg)) {
        const proceed = window.confirm(
          'هذه الدفعة ليست مرتبطة بحساب بنكي (bank_account_id مفقود). ' +
          'هل تريد إرفاق الحساب البنكي الافتراضي تلقائياً ثم إعادة المحاولة؟'
        );
        if (proceed) {
          try {
            const r = await axiosInstance.post(`/payments/${id}/attach-bank`, {});
            const bn = r?.data?.bank_account_name;
            toast.success(`تم ربط الحساب البنكي: ${bn} — جارٍ إعادة الاعتماد…`);
            await axiosInstance.post(`/approvals/${type}/${id}/approve`);
            toast.success('Approved successfully!');
            setSelectedItem(null);
            setEditMode(false);
            fetchPendingApprovals();
            return;
          } catch (e2) {
            const d = e2.response?.data?.detail;
            if (d?.code === 'AMBIGUOUS_BANK' && Array.isArray(d.available_bank_accounts)) {
              const opts = d.available_bank_accounts
                .map((b, i) => `${i + 1}. ${b.bank_name} (${b.account_name || 'بدون اسم'})`)
                .join('\n');
              const choice = window.prompt(
                `يوجد عدة حسابات بنكية — اختر الرقم:\n${opts}`, '1');
              const idx = Number(choice) - 1;
              const chosen = d.available_bank_accounts[idx];
              if (chosen) {
                try {
                  await axiosInstance.post(`/payments/${id}/attach-bank`,
                    { bank_account_id: chosen.id });
                  toast.success(`ربط الحساب: ${chosen.bank_name}`);
                  await axiosInstance.post(`/approvals/${type}/${id}/approve`);
                  toast.success('Approved successfully!');
                  setSelectedItem(null);
                  setEditMode(false);
                  fetchPendingApprovals();
                  return;
                } catch (e3) {
                  toast.error(e3.response?.data?.detail || 'Failed to approve');
                  return;
                }
              }
            }
            toast.error(typeof d === 'string' ? d : (d?.message || 'Attach-bank failed'));
            return;
          }
        }
      }
      toast.error(msg);
    }
  };

  const handleReject = async (type, id) => {
    try {
      await axiosInstance.post(`/approvals/${type}/${id}/reject`);
      toast.success('Rejected successfully');
      setSelectedItem(null);
      setEditMode(false);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const openDetails = (item, type) => {
    setSelectedItem(item);
    setSelectedType(type);
    setEditData({ ...item });
    setEditMode(false);
    // Supplier-bills: seed the PO editor mode from the existing record —
    // manual mode if the bill carries a PO Number but no linked PO doc.
    if (type === 'supplier-bills') {
      setPoManualMode(
        !item.purchase_order_id && !!item.purchase_order_no,
      );
    } else {
      setPoManualMode(false);
    }
  };

  const closeDetails = () => {
    setSelectedItem(null);
    setSelectedType(null);
    setEditMode(false);
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Recalculate payroll totals
      if (selectedType === 'payroll') {
        const baseSalary = parseFloat(field === 'base_salary' ? value : newData.base_salary) || 0;
        const housingAllowance = parseFloat(field === 'housing_allowance' ? value : newData.housing_allowance) || 0;
        const transportAllowance = parseFloat(field === 'transport_allowance' ? value : newData.transport_allowance) || 0;
        const foodAllowance = parseFloat(field === 'food_allowance' ? value : newData.food_allowance) || 0;
        const otherAllowances = parseFloat(field === 'other_allowances' ? value : newData.other_allowances) || 0;
        const overtimeAmount = parseFloat(field === 'overtime_amount' ? value : newData.overtime_amount) || 0;
        const bonuses = parseFloat(field === 'bonuses' ? value : newData.bonuses) || 0;
        
        const absenceDeduction = parseFloat(field === 'absence_deduction' ? value : newData.absence_deduction) || 0;
        const lateDeduction = parseFloat(field === 'late_deduction' ? value : newData.late_deduction) || 0;
        const otherDeductions = parseFloat(field === 'other_deductions' ? value : newData.other_deductions) || 0;
        const loanDeduction = parseFloat(field === 'loan_deduction' ? value : newData.loan_deduction) || 0;
        const gosiEmployee = parseFloat(field === 'gosi_employee' ? value : newData.gosi_employee) || 0;
        
        const totalEarnings = baseSalary + housingAllowance + transportAllowance + foodAllowance + otherAllowances + overtimeAmount + bonuses;
        const totalDeductions = absenceDeduction + lateDeduction + otherDeductions + loanDeduction + gosiEmployee;
        const netSalary = totalEarnings - totalDeductions;
        
        newData.total_earnings = totalEarnings;
        newData.total_deductions = totalDeductions;
        newData.net_salary = netSalary;
      }
      
      return newData;
    });
  };

  const handleSaveEdit = async () => {
    try {
      const apiEndpoints = {
        'customers': '/customers',
        'suppliers': '/suppliers',
        'quotations': '/quotations',
        'invoices': '/invoices',
        'purchase-orders': '/purchase-orders',
        'projects': '/projects',
        'expenses': '/expenses',
        'payments': '/payments',
        'employees': '/employees',
        'payroll': '/payroll',
        'employee-loans': '/employee-loans',
        'expense-reports': '/expense-reports',
        'inventory': '/inventory',
        'supplier-bills': '/supplier-bills'
      };
      
      const endpoint = apiEndpoints[selectedType];
      if (endpoint) {
        let dataToSend = { ...editData };
        
        // Recalculate totals if items exist (for quotations, invoices, etc.)
        if (editData.items && editData.items.length > 0) {
          const updatedItems = editData.items.map(item => ({
            ...item,
            total: (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)
          }));
          const subtotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
          const vatRate = 0.15; // 15% VAT
          const vatAmount = subtotal * vatRate;
          const total = subtotal + vatAmount;
          
          dataToSend = {
            ...editData,
            items: updatedItems,
            subtotal: subtotal,
            vat_amount: vatAmount,
            tax_amount: vatAmount,
            total: total
          };
        }
        
        // For payroll, totals are already calculated in handleEditChange
        // Just ensure we send the right format
        if (selectedType === 'payroll') {
          dataToSend = {
            employee_id: editData.employee_id,
            period_start: editData.period_start,
            period_end: editData.period_end,
            base_salary: parseFloat(editData.base_salary) || 0,
            housing_allowance: parseFloat(editData.housing_allowance) || 0,
            transport_allowance: parseFloat(editData.transport_allowance) || 0,
            food_allowance: parseFloat(editData.food_allowance) || 0,
            other_allowances: parseFloat(editData.other_allowances) || 0,
            overtime_hours: parseFloat(editData.overtime_hours) || 0,
            overtime_rate: parseFloat(editData.overtime_rate) || 1.5,
            overtime_amount: parseFloat(editData.overtime_amount) || 0,
            bonuses: parseFloat(editData.bonuses) || 0,
            bonus_reason: editData.bonus_reason || '',
            absence_days: parseInt(editData.absence_days) || 0,
            absence_deduction: parseFloat(editData.absence_deduction) || 0,
            late_deduction: parseFloat(editData.late_deduction) || 0,
            other_deductions: parseFloat(editData.other_deductions) || 0,
            deduction_reason: editData.deduction_reason || '',
            loan_deduction: parseFloat(editData.loan_deduction) || 0,
            loan_balance: parseFloat(editData.loan_balance) || 0,
            gosi_employee: parseFloat(editData.gosi_employee) || 0,
            gosi_company: parseFloat(editData.gosi_company) || 0,
            total_earnings: parseFloat(editData.total_earnings) || 0,
            total_deductions: parseFloat(editData.total_deductions) || 0,
            net_salary: parseFloat(editData.net_salary) || 0,
            notes: editData.notes || ''
          };
        }
        
        // For employee loans
        if (selectedType === 'employee-loans') {
          dataToSend = {
            employee_id: editData.employee_id,
            loan_type: editData.loan_type,
            amount: parseFloat(editData.amount) || 0,
            monthly_deduction: parseFloat(editData.monthly_deduction) || 0,
            reason: editData.reason || '',
            start_date: editData.start_date
          };
        }
        
        await axiosInstance.put(`${endpoint}/${selectedItem.id}`, dataToSend);
        toast.success('Updated successfully!');
        setEditMode(false);
        fetchPendingApprovals();
        // Update selected item with new calculated values
        setSelectedItem({ ...selectedItem, ...editData });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...(editData.items || [])];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
      total: field === 'quantity' || field === 'unit_price' 
        ? (parseFloat(field === 'quantity' ? value : newItems[index].quantity) || 0) * 
          (parseFloat(field === 'unit_price' ? value : newItems[index].unit_price) || 0)
        : newItems[index].total
    };
    
    // Recalculate subtotal and total
    const subtotal = newItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const vatAmount = subtotal * 0.15;
    const total = subtotal + vatAmount;
    
    setEditData(prev => ({
      ...prev,
      items: newItems,
      subtotal: subtotal,
      vat_amount: vatAmount,
      tax_amount: vatAmount,
      total: total
    }));
  };

  const renderEditableItemsTable = (items) => {
    const currentItems = editMode ? (editData.items || items) : items;
    
    return (
      <div className="mt-4">
        <h4 className="font-semibold text-gray-800 mb-2">Items</h4>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-1 text-left">Description</th>
              <th className="px-2 py-1 text-right w-24">Qty</th>
              <th className="px-2 py-1 text-right w-32">Price</th>
              <th className="px-2 py-1 text-right w-32">Total</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((item, idx) => (
              <tr key={idx} className="border-b">
                <td className="px-2 py-1">
                  {editMode ? (
                    <input
                      type="text"
                      value={item.description || ''}
                      onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  ) : (
                    item.description
                  )}
                </td>
                <td className="px-2 py-1 text-right">
                  {editMode ? (
                    <input
                      type="number"
                      value={item.quantity || ''}
                      onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                      className="w-20 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                    />
                  ) : (
                    item.quantity
                  )}
                </td>
                <td className="px-2 py-1 text-right">
                  {editMode ? (
                    <input
                      type="number"
                      value={item.unit_price || ''}
                      onChange={(e) => handleItemChange(idx, 'unit_price', e.target.value)}
                      className="w-28 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                    />
                  ) : (
                    item.unit_price?.toLocaleString()
                  )}
                </td>
                <td className="px-2 py-1 text-right font-medium">
                  {(editMode ? (editData.items?.[idx]?.total || item.total) : item.total)?.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
          {editMode && (
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td colSpan="3" className="px-2 py-1 text-right">Subtotal:</td>
                <td className="px-2 py-1 text-right">{(editData.subtotal || 0).toLocaleString()} SAR</td>
              </tr>
              <tr>
                <td colSpan="3" className="px-2 py-1 text-right">VAT (15%):</td>
                <td className="px-2 py-1 text-right">{(editData.vat_amount || editData.tax_amount || 0).toLocaleString()} SAR</td>
              </tr>
              <tr className="text-green-600">
                <td colSpan="3" className="px-2 py-1 text-right">Total:</td>
                <td className="px-2 py-1 text-right">{(editData.total || 0).toLocaleString()} SAR</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-12">Loading approvals...</div>;
  }

  const tabs = [
    { id: 'customers', label: 'Customers', icon: Users, count: pendingData.customers?.length || 0, type: 'customers' },
    { id: 'suppliers', label: 'Suppliers', icon: Truck, count: pendingData.suppliers?.length || 0, type: 'suppliers' },
    { id: 'quotations', label: 'Quotations', icon: FileText, count: pendingData.quotations?.length || 0, type: 'quotations' },
    { id: 'sales_quotations', label: 'Sales Quotations', icon: ClipboardList, count: pendingData.sales_quotations?.length || 0, type: 'sales-quotations' },
    { id: 'invoices', label: 'Invoices', icon: FileSpreadsheet, count: pendingData.invoices?.length || 0, type: 'invoices' },
    { id: 'purchase_orders', label: 'Purchase Orders', icon: ShoppingCart, count: pendingData.purchase_orders?.length || 0, type: 'purchase-orders' },
    { id: 'supplier_bills', label: 'Supplier Bills', icon: Receipt, count: pendingData.supplier_bills?.length || 0, type: 'supplier-bills' },
    { id: 'projects', label: 'Projects', icon: FolderKanban, count: pendingData.projects?.length || 0, type: 'projects' },
    { id: 'inventory', label: 'Inventory', icon: Package, count: pendingData.inventory?.length || 0, type: 'inventory' },
    { id: 'payments', label: 'Payments', icon: CreditCard, count: pendingData.payments?.length || 0, type: 'payments' },
    { id: 'employees', label: 'Employees', icon: UserCog, count: pendingData.employees?.length || 0, type: 'employees' },
    { id: 'payroll', label: 'Payroll', icon: Wallet, count: pendingData.payroll?.length || 0, type: 'payroll' },
    { id: 'employee_loans', label: 'Loans', icon: CreditCard, count: pendingData.employee_loans?.length || 0, type: 'employee-loans' },
    { id: 'expense_reports', label: 'Expense Reports', icon: ClipboardList, count: pendingData.expense_reports?.length || 0, type: 'expense-reports' }
  ];

  const renderApprovalCard = (item, type, icon, title, subtitle, createdBy) => (
    <div 
      key={item.id} 
      className="p-4 flex items-center justify-between hover:bg-gray-50 border-b border-gray-100 cursor-pointer"
      onClick={() => openDetails(item, type)}
    >
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            type === 'customers' ? 'bg-blue-100' : 
            type === 'suppliers' ? 'bg-orange-100' :
            type === 'quotations' ? 'bg-purple-100' :
            type === 'sales-quotations' ? 'bg-sky-100' :
            type === 'invoices' ? 'bg-green-100' :
            type === 'purchase-orders' ? 'bg-yellow-100' :
            type === 'supplier-bills' ? 'bg-rose-100' :
            type === 'projects' ? 'bg-indigo-100' :
            type === 'inventory' ? 'bg-amber-100' :
            type === 'payments' ? 'bg-cyan-100' :
            type === 'employees' ? 'bg-pink-100' :
            type === 'expense-reports' ? 'bg-teal-100' :
            'bg-red-100'
          }`}>
            {React.createElement(icon, { className: `w-5 h-5 ${
              type === 'customers' ? 'text-blue-600' : 
              type === 'suppliers' ? 'text-orange-600' :
              type === 'quotations' ? 'text-purple-600' :
              type === 'sales-quotations' ? 'text-sky-600' :
              type === 'invoices' ? 'text-green-600' :
              type === 'purchase-orders' ? 'text-yellow-600' :
              type === 'supplier-bills' ? 'text-rose-600' :
              type === 'projects' ? 'text-indigo-600' :
              type === 'inventory' ? 'text-amber-600' :
              type === 'payments' ? 'text-cyan-600' :
              type === 'employees' ? 'text-pink-600' :
              type === 'expense-reports' ? 'text-teal-600' :
              'text-red-600'
            }`})}
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{title}</h4>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
        </div>
        {createdBy && (
          <div className="mt-2 ml-13 flex items-center gap-1 text-xs text-gray-500">
            <UserCircle className="w-4 h-4" />
            <span>Created by: <strong className="text-gray-700">{createdBy}</strong></span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm flex items-center gap-1">
          <Clock className="w-4 h-4" /> Pending
        </span>
        <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1">
          <Eye className="w-4 h-4" /> View
        </button>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="p-12 text-center text-gray-500">
      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
      <p>No pending approvals in this category</p>
    </div>
  );

  // Detail Modal Content based on type
  const renderDetailContent = () => {
    if (!selectedItem || !selectedType) return null;

    const renderField = (label, field, editable = true) => {
      const value = editMode ? editData[field] : selectedItem[field];
      const isNumericField = ['base_salary', 'housing_allowance', 'transport_allowance', 'food_allowance', 
        'other_allowances', 'overtime_amount', 'bonuses', 'absence_deduction', 'late_deduction', 
        'other_deductions', 'loan_deduction', 'gosi_employee', 'amount', 'monthly_deduction',
        'amount_paid'].includes(field);
      
      return (
        <div key={field} className="flex justify-between py-2 border-b border-gray-100 items-center">
          <span className="text-gray-600 font-medium">{label}</span>
          {editMode && editable ? (
            <input
              type={isNumericField ? "number" : "text"}
              value={value || ''}
              onChange={(e) => handleEditChange(field, isNumericField ? parseFloat(e.target.value) || 0 : e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-1/2 text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <span className="text-gray-900">{selectedItem[field] || '-'}</span>
          )}
        </div>
      );
    };

    const renderStaticField = (label, value) => (
      <div className="flex justify-between py-2 border-b border-gray-100 items-center">
        <span className="text-gray-600 font-medium">{label}</span>
        <span className="text-gray-900">{value || '-'}</span>
      </div>
    );

    // Editable money field with automatic total recompute (invoices).
    const renderMoneyEdit = (label, field) => {
      const current = editMode
        ? (editData[field] ?? (field === 'vat_amount' ? editData.tax_amount : undefined))
        : (selectedItem[field] ?? (field === 'vat_amount' ? selectedItem.tax_amount : undefined));
      if (!editMode) {
        return renderStaticField(label, `${(parseFloat(current) || 0).toLocaleString()} SAR`);
      }
      return (
        <div key={field} className="flex justify-between py-2 border-b border-gray-100 items-center">
          <span className="text-gray-600 font-medium">{label}</span>
          <input
            type="number"
            step="0.01"
            data-testid={`approval-edit-${field}`}
            value={current ?? ''}
            onChange={(e) => {
              const v = parseFloat(e.target.value) || 0;
              const next = { ...editData, [field]: v };
              if (field === 'vat_amount') next.tax_amount = v;
              const sub = parseFloat(field === 'subtotal' ? v : (next.subtotal ?? 0)) || 0;
              const vat = parseFloat(field === 'vat_amount' ? v : (next.vat_amount ?? next.tax_amount ?? 0)) || 0;
              if (field === 'subtotal' || field === 'vat_amount') {
                next.total = Math.round((sub + vat) * 100) / 100;
              }
              setEditData(next);
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm w-40 text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      );
    };

    // Helper function to get correct file URL
    const getFileUrl = (url) => {
      if (!url) return null;
      if (url.startsWith('http')) return url;
      if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
      return `${process.env.REACT_APP_BACKEND_URL}/api${url.startsWith('/') ? '' : '/'}${url}`;
    };

    // Open file in preview modal
    const openFilePreview = (url, label) => {
      const fileUrl = getFileUrl(url);
      const isPdf = url.toLowerCase().endsWith('.pdf');
      setFilePreview({ url: fileUrl, label, isPdf });
    };

    // Render attachment section for items with files
    const renderAttachment = (url, label = "Attachment") => {
      if (!url) return null;
      const fileUrl = getFileUrl(url);
      const isPdf = url.toLowerCase().endsWith('.pdf');
      
      return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            {label}
          </h4>
          <div className="flex gap-2">
            <button
              onClick={() => openFilePreview(url, label)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-colors ${
                isPdf ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <Eye className="w-4 h-4" />
              {isPdf ? 'View PDF' : 'View File'}
            </button>
            <a
              href={fileUrl}
              download
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </a>
          </div>
          {!isPdf && (
            <div className="mt-3">
              <img 
                src={fileUrl} 
                alt={label}
                className="max-w-full h-48 object-contain rounded border border-gray-200"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
        </div>
      );
    };

    // Render multiple customer files (National Address, CR, VAT)
    const renderCustomerFiles = () => {
      const files = [
        { url: selectedItem.national_address_file, label: "National Address File" },
        { url: selectedItem.cr_file, label: "CR Certificate" },
        { url: selectedItem.vat_file, label: "VAT Certificate" }
      ].filter(f => f.url);
      
      if (files.length === 0) return null;
      
      return (
        <div className="mt-4 space-y-3">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Attached Documents
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {files.map((file, idx) => {
              const isPdf = file.url.toLowerCase().endsWith('.pdf');
              return (
                <button
                  key={idx}
                  onClick={() => openFilePreview(file.url, file.label)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-white text-sm font-medium transition-colors ${
                    isPdf ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  <Eye className="w-4 h-4" />
                  {file.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    };

    switch (selectedType) {
      case 'customers':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Customer' : 'Customer Details'}
            </h3>
            <div className="space-y-1">
              {renderField("Name (Arabic)", "name_arabic")}
              {renderField("Name (English)", "name_english")}
              {renderField("Email", "email")}
              {renderField("Phone", "phone")}
              {renderField("Mobile", "mobile")}
              {renderField("Address", "address")}
              {renderField("CR Number", "cr_number")}
              {renderField("VAT Number", "vat_number")}
              {renderField("Contact Person", "contact_person")}
              {renderField("Notes", "notes")}
              {renderStaticField("Created By", selectedItem.created_by_name)}
            </div>
            {renderCustomerFiles()}
          </>
        );

      case 'suppliers':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Supplier' : 'Supplier Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Supplier No", selectedItem.supplier_number)}
              {renderField("Name (Arabic)", "name_arabic")}
              {renderField("Name (English)", "name_english")}
              {renderField("Email", "email")}
              {renderField("Phone", "phone")}
              {renderField("Mobile", "mobile")}
              {renderField("Address", "address")}
              {renderField("CR Number", "cr_number")}
              {renderField("VAT Number", "vat_number")}
              {renderField("Contact Person", "contact_person")}
              {renderField("Bank Name", "bank_name")}
              {renderField("Bank Account", "bank_account")}
              {renderField("IBAN", "bank_iban")}
              {renderStaticField("Created By", selectedItem.created_by_name)}
            </div>
          </>
        );

      case 'quotations':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Quotation' : 'Quotation Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Quotation No", selectedItem.quotation_no)}
              {renderStaticField("Customer", selectedItem.customer_name)}
              {renderField("Subject", "subject")}
              {renderField("Date", "quotation_date")}
              {renderField("Valid Until", "valid_until")}
              {renderStaticField("Subtotal", `${selectedItem.subtotal?.toLocaleString()} SAR`)}
              {renderStaticField("VAT", `${selectedItem.vat_amount?.toLocaleString()} SAR`)}
              {renderStaticField("Total", `${selectedItem.total?.toLocaleString()} SAR`)}
              {renderStaticField("Status", selectedItem.status)}
              {renderStaticField("Created By", selectedItem.user_name)}
            </div>
            {selectedItem.items?.length > 0 && renderEditableItemsTable(selectedItem.items)}
          </>
        );

      case 'invoices':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Invoice' : 'Invoice Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Invoice No", selectedItem.invoice_number)}
              {renderStaticField("Customer", selectedItem.customer_name || selectedItem.client_name)}
              {selectedItem.project_name && renderStaticField("Project", selectedItem.project_name)}
              {renderField("Invoice Date", "invoice_date")}
              {renderField("Due Date", "due_date")}
              {renderField("Payment Terms", "payment_terms")}
              {renderMoneyEdit("Subtotal", "subtotal")}
              {renderMoneyEdit("VAT", "vat_amount")}
              {editMode
                ? renderStaticField("Total (auto)", `${(editData.total ?? selectedItem.total)?.toLocaleString()} SAR`)
                : renderStaticField("Total", `${selectedItem.total?.toLocaleString()} SAR`)}
              {renderMoneyEdit("Amount Paid", "amount_paid")}
              {renderStaticField("Status", selectedItem.status)}
              {renderField("Notes", "notes")}
              {renderStaticField("Created By", selectedItem.created_by_name || selectedItem.user_name)}
            </div>
            {selectedItem.items?.length > 0 && renderEditableItemsTable(selectedItem.items)}
          </>
        );

      case 'sales-quotations':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Sales Quotation Details
            </h3>
            <div className="space-y-1">
              {renderStaticField("Quotation No", selectedItem.quotation_number)}
              {renderStaticField("Customer", selectedItem.customer_name)}
              {renderStaticField("Project", selectedItem.project_name)}
              {renderStaticField("Type of Work", selectedItem.type_of_work)}
              {renderStaticField("Quote Date", selectedItem.quote_date)}
              {renderStaticField("Expiry Date", selectedItem.expiry_date)}
              {renderStaticField("Payment Terms", selectedItem.payment_terms)}
              {renderStaticField("Delivery Time", selectedItem.delivery_time)}
              {renderStaticField("Subtotal", `${Number(selectedItem.subtotal || 0).toLocaleString()} ${selectedItem.currency || 'SAR'}`)}
              {renderStaticField("VAT (15%)", `${Number(selectedItem.tax || 0).toLocaleString()} ${selectedItem.currency || 'SAR'}`)}
              {renderStaticField("Total", `${Number(selectedItem.total || 0).toLocaleString()} ${selectedItem.currency || 'SAR'}`)}
              {renderStaticField("Status", selectedItem.status)}
              {renderStaticField("Submitted By", selectedItem.created_by_name || selectedItem.submitted_by_name)}
            </div>
            {selectedItem.items?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold text-gray-800 mb-2">Items ({selectedItem.items.length})</h4>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Unit Price</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItem.items.map((it, idx) => (
                        <tr key={idx} className="border-t border-gray-100">
                          <td className="px-3 py-2">{it.sales_description || it.description || '-'}</td>
                          <td className="px-3 py-2 text-right">{it.qty}</td>
                          <td className="px-3 py-2 text-right">{Number(it.unit_price || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-medium">
                            {Number((it.qty || 0) * (it.unit_price || 0)).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        );
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Invoice' : 'Invoice Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Invoice No", selectedItem.invoice_number)}
              {renderStaticField("Client", selectedItem.client_name)}
              {renderField("Client (Arabic)", "client_name_arabic")}
              {renderField("Invoice Date", "invoice_date")}
              {renderField("Due Date", "due_date")}
              {renderField("PO Number", "purchase_order_no")}
              {!editMode && renderStaticField("Subtotal", `${selectedItem.subtotal?.toLocaleString()} SAR`)}
              {!editMode && renderStaticField("VAT", `${selectedItem.tax_amount?.toLocaleString()} SAR`)}
              {!editMode && renderStaticField("Total", `${selectedItem.total?.toLocaleString()} SAR`)}
              {renderStaticField("Amount Paid", `${selectedItem.amount_paid?.toLocaleString()} SAR`)}
              {renderStaticField("Amount Remaining", `${selectedItem.amount_remaining?.toLocaleString()} SAR`)}
              {renderStaticField("Status", selectedItem.status)}
              {renderStaticField("Created By", selectedItem.user_name)}
            </div>
            {selectedItem.items?.length > 0 && renderEditableItemsTable(selectedItem.items)}
          </>
        );

      case 'purchase-orders':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Purchase Order' : 'Purchase Order Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("PO Number", selectedItem.po_number)}
              {renderStaticField("Supplier", selectedItem.supplier_name)}
              {renderField("PO Date", "po_date")}
              {renderField("Expected Delivery", "expected_delivery_date")}
              {renderStaticField("Subtotal", `${selectedItem.subtotal?.toLocaleString()} SAR`)}
              {renderStaticField("VAT", `${selectedItem.vat_amount?.toLocaleString()} SAR`)}
              {renderStaticField("Total", `${selectedItem.total?.toLocaleString()} SAR`)}
              {renderStaticField("Status", selectedItem.status)}
              {renderStaticField("Created By", selectedItem.user_name)}
            </div>
            {selectedItem.items?.length > 0 && renderEditableItemsTable(selectedItem.items)}
            {renderAttachment(selectedItem.attachment_url, "Purchase Order Attachment")}
          </>
        );

      case 'projects':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Project' : 'Project Details'}
            </h3>
            <div className="space-y-1">
              {renderField("Project Name", "name")}
              {renderStaticField("Client", selectedItem.client_name)}
              {renderField("Description", "description")}
              {renderField("Start Date", "start_date")}
              {renderField("End Date", "end_date")}
              {renderStaticField("Budget", `${selectedItem.budget?.toLocaleString()} SAR`)}
              {renderStaticField("Spent", `${selectedItem.spent?.toLocaleString()} SAR`)}
              {renderStaticField("Status", selectedItem.status)}
              {renderStaticField("Created By", selectedItem.user_name)}
            </div>
          </>
        );

      case 'expenses':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Expense' : 'Expense Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Expense No", selectedItem.expense_no)}
              {renderField("From", "expense_from")}
              {renderField("Date", "expense_date")}
              {renderField("Type", "expense_type")}
              {renderField("Category", "category")}
              {renderField("Vendor", "vendor")}
              {renderStaticField("Amount", `${selectedItem.amount?.toLocaleString()} SAR`)}
              {renderField("Payment Method", "payment_method")}
              {renderField("Description", "description")}
              {renderField("Notes", "notes")}
              {renderStaticField("Created By", selectedItem.user_name)}
            </div>
            {renderAttachment(selectedItem.photo_url, "Expense Receipt")}
          </>
        );

      case 'payments':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Payment' : 'Payment Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Payment No", selectedItem.payment_no)}
              {renderField("Date", "payment_date")}
              {renderStaticField("Type", selectedItem.payment_type)}
              {renderField("Method", "payment_method")}
              {renderField("Amount", "amount")}
              {renderStaticField("Customer", selectedItem.customer_name)}
              {renderStaticField("Supplier", selectedItem.supplier_name)}
              {renderStaticField("Invoice", selectedItem.invoice_no)}
              {renderField("Reference", "reference_no")}
              {renderStaticField("Debit Account", selectedItem.debit_account_name)}
              {renderStaticField("Credit Account", selectedItem.credit_account_name)}
              {renderField("Description", "description")}
              {renderStaticField("Created By", selectedItem.user_name)}
            </div>
            {renderAttachment(selectedItem.attachment_url, "Payment Attachment")}
          </>
        );

      case 'employees':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Employee' : 'Employee Details'}
            </h3>
            <div className="space-y-1">
              {renderField("Full Name", "full_name")}
              {renderField("Email", "email")}
              {renderField("Phone", "phone")}
              {renderField("Position", "position")}
              {renderField("Department", "department")}
              {renderStaticField("Salary", `${selectedItem.salary?.toLocaleString()} SAR`)}
              {renderField("Hire Date", "hire_date")}
              {renderStaticField("Status", selectedItem.status)}
              {renderStaticField("Created By", selectedItem.created_by_name)}
            </div>
          </>
        );

      case 'payroll':
        const payrollData = editMode ? editData : selectedItem;
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? 'Edit Payroll' : 'Payroll Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Payroll No", selectedItem.payroll_number)}
              {renderStaticField("Employee", selectedItem.employee_name)}
              {renderStaticField("Position", selectedItem.employee_position)}
              {renderStaticField("Department", selectedItem.employee_department)}
              {renderField("Period Start", "period_start")}
              {renderField("Period End", "period_end")}
            </div>
            <div className="mt-4 p-3 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-700 mb-2">Earnings</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {renderField("Base Salary", "base_salary")}
                {renderField("Housing Allowance", "housing_allowance")}
                {renderField("Transport Allowance", "transport_allowance")}
                {renderField("Food Allowance", "food_allowance")}
                {renderField("Other Allowances", "other_allowances")}
                {renderField("Overtime Amount", "overtime_amount")}
                {renderField("Bonuses", "bonuses")}
                <div className="col-span-2 border-t pt-2 flex justify-between font-bold text-green-700">
                  <span>Total Earnings:</span>
                  <span>{(payrollData.total_earnings || 0).toLocaleString()} SAR</span>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-red-50 rounded-lg">
              <h4 className="font-semibold text-red-700 mb-2">Deductions</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {renderField("Absence Deduction", "absence_deduction")}
                {renderField("Late Deduction", "late_deduction")}
                {renderField("Other Deductions", "other_deductions")}
                {renderField("Loan Deduction", "loan_deduction")}
                {renderField("GOSI Employee", "gosi_employee")}
                <div className="col-span-2 border-t pt-2 flex justify-between font-bold text-red-700">
                  <span>Total Deductions:</span>
                  <span>{(payrollData.total_deductions || 0).toLocaleString()} SAR</span>
                </div>
              </div>
            </div>
            <div className="mt-4 p-3 bg-purple-100 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-bold text-purple-700 text-lg">Net Salary:</span>
                <span className="font-bold text-purple-700 text-xl">{(payrollData.net_salary || 0).toLocaleString()} SAR</span>
              </div>
            </div>
            <div className="mt-4">
              {renderField("Notes", "notes")}
            </div>
            {renderStaticField("Created By", selectedItem.created_by_name)}
          </>
        );

      case 'employee-loans':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? 'Edit Loan/Advance' : 'Loan/Advance Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Loan No", selectedItem.loan_number)}
              {renderStaticField("Employee", selectedItem.employee_name)}
              {renderStaticField("Type", selectedItem.loan_type === 'loan' ? 'Loan' : 'Advance')}
              {renderField("Amount", "amount")}
              {renderField("Monthly Deduction", "monthly_deduction")}
              {renderStaticField("Remaining Balance", `${selectedItem.remaining_balance?.toLocaleString()} SAR`)}
              {renderField("Start Date", "start_date")}
              {renderField("Reason", "reason")}
              {renderStaticField("Status", selectedItem.status)}
              {renderStaticField("Created By", selectedItem.created_by_name)}
            </div>
          </>
        );

      case 'expense-reports':
        const expensesData = editMode ? (editData.expenses || selectedItem.expenses) : selectedItem.expenses;
        const calculateExpenseTotal = (amount, vat) => (parseFloat(amount) || 0) + (parseFloat(vat) || 0);
        const totalAmount = expensesData?.reduce((sum, e) => sum + calculateExpenseTotal(e.amount, e.vat), 0) || 0;
        const totalAmountOnly = expensesData?.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0) || 0;
        const totalVat = expensesData?.reduce((sum, e) => sum + (parseFloat(e.vat) || 0), 0) || 0;
        
        const handleExpenseChange = (idx, field, value) => {
          const updatedExpenses = [...(editData.expenses || selectedItem.expenses)];
          updatedExpenses[idx] = { ...updatedExpenses[idx], [field]: value };
          
          // Auto-calculate total_with_vat
          if (field === 'amount' || field === 'vat') {
            const amount = field === 'amount' ? parseFloat(value) || 0 : parseFloat(updatedExpenses[idx].amount) || 0;
            const vat = field === 'vat' ? parseFloat(value) || 0 : parseFloat(updatedExpenses[idx].vat) || 0;
            updatedExpenses[idx].total_with_vat = amount + vat;
          }
          
          // Update total amount
          const newTotal = updatedExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0) + (parseFloat(e.vat) || 0), 0);
          
          setEditData({ 
            ...editData, 
            expenses: updatedExpenses,
            total_amount: newTotal
          });
        };
        
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Expense Report' : 'Expense Report Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Report No", selectedItem.report_no)}
              {renderStaticField("Report Date", selectedItem.report_date)}
              {renderStaticField("Period", `${selectedItem.period_start} to ${selectedItem.period_end}`)}
              {renderStaticField("Total Amount", `${(editMode ? totalAmount : selectedItem.total_amount)?.toLocaleString()} SAR`)}
              {renderStaticField("Expenses Count", selectedItem.expenses_count)}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-green-600 font-medium">Debit Account</p>
                    <p className="font-semibold text-green-700">{selectedItem.debit_account_name}</p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-xs text-red-600 font-medium">Credit Account</p>
                    <p className="font-semibold text-red-700">{selectedItem.credit_account_name}</p>
                  </div>
                </div>
              </div>
              {renderStaticField("Journal Reference", selectedItem.journal_reference)}
              {renderStaticField("Created By", selectedItem.created_by_name || selectedItem.user_name)}
              
              {/* Expense Details Table with Invoice */}
              {expensesData && expensesData.length > 0 && (
                <div className="mt-4">
                  <h5 className="font-semibold text-gray-700 mb-2">
                    Expenses Included: {editMode && <span className="text-blue-600 text-sm font-normal">(Click to edit)</span>}
                  </h5>
                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Date</th>
                          <th className="text-left p-2">Expense From</th>
                          <th className="text-left p-2">Type</th>
                          <th className="text-right p-2">Amount</th>
                          <th className="text-right p-2">VAT</th>
                          <th className="text-right p-2">Total</th>
                          <th className="text-center p-2">Invoice</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expensesData.map((exp, idx) => (
                          <tr key={idx} className="border-t border-gray-100">
                            <td className="p-2">{exp.expense_date}</td>
                            <td className="p-2">
                              {editMode ? (
                                <input
                                  type="text"
                                  value={exp.expense_from || ''}
                                  onChange={(e) => handleExpenseChange(idx, 'expense_from', e.target.value)}
                                  className="w-full px-2 py-1 border rounded text-sm"
                                  placeholder="Vendor name"
                                />
                              ) : (exp.expense_from || '-')}
                            </td>
                            <td className="p-2">{exp.expense_type || '-'}</td>
                            <td className="p-2 text-right">
                              {editMode ? (
                                <input
                                  type="number"
                                  value={exp.amount || 0}
                                  onChange={(e) => handleExpenseChange(idx, 'amount', e.target.value)}
                                  className="w-20 px-2 py-1 border rounded text-sm text-right"
                                  min="0"
                                  step="0.01"
                                />
                              ) : `${exp.amount?.toLocaleString()} SAR`}
                            </td>
                            <td className="p-2 text-right">
                              {editMode ? (
                                <input
                                  type="number"
                                  value={exp.vat || 0}
                                  onChange={(e) => handleExpenseChange(idx, 'vat', e.target.value)}
                                  className="w-20 px-2 py-1 border rounded text-sm text-right"
                                  min="0"
                                  step="0.01"
                                />
                              ) : `${exp.vat?.toLocaleString() || 0} SAR`}
                            </td>
                            <td className="p-2 text-right font-medium bg-gray-50">
                              {editMode 
                                ? `${calculateExpenseTotal(exp.amount, exp.vat).toLocaleString()} SAR`
                                : `${(exp.total_with_vat || exp.amount)?.toLocaleString()} SAR`
                              }
                            </td>
                            <td className="p-2 text-center">
                              {exp.photo_url ? (
                                exp.photo_url.toLowerCase().endsWith('.pdf') ? (
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}${exp.photo_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-red-600 hover:text-red-800 flex items-center justify-center gap-1"
                                    title="View PDF Invoice"
                                  >
                                    <FileText className="w-4 h-4" />
                                    <span className="text-xs">PDF</span>
                                  </a>
                                ) : (
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}${exp.photo_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="View Invoice Image"
                                  >
                                    <img 
                                      src={`${process.env.REACT_APP_BACKEND_URL}${exp.photo_url}`}
                                      alt="Invoice"
                                      className="w-10 h-10 object-cover rounded border border-gray-200 mx-auto hover:border-blue-400"
                                    />
                                  </a>
                                )
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100 font-semibold">
                        <tr>
                          <td colSpan="3" className="p-2 text-right">Totals:</td>
                          <td className="p-2 text-right">{totalAmountOnly.toLocaleString()} SAR</td>
                          <td className="p-2 text-right">{totalVat.toLocaleString()} SAR</td>
                          <td className="p-2 text-right">{totalAmount.toLocaleString()} SAR</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        );

      case 'inventory':
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Inventory Item' : 'Inventory Item Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Item Number", selectedItem.item_number)}
              {renderField("Item Name", "item_name")}
              {renderField("Category", "category")}
              {renderField("Quantity", "quantity")}
              {renderField("Unit", "unit")}
              {renderField("Unit Price", "unit_price")}
              {renderStaticField("Total Value", `${selectedItem.total_value?.toLocaleString()} SAR`)}
              {renderField("Supplier", "supplier")}
              {renderField("Reorder Level", "reorder_level")}
              {renderStaticField("Created By", selectedItem.created_by_name)}
            </div>
          </>
        );

      case 'supplier-bills': {
        // PO Number editor — dropdown (preferred) ↔ manual free-text.
        // Linked PO always wins on the backend; the manual field is a
        // fallback for legacy / paper-PO bills.
        const supplierPOs = allPurchaseOrders.filter(
          (po) => po.supplier_id === (editData.supplier_id || selectedItem.supplier_id),
        );
        const switchToManual = () => {
          setPoManualMode(true);
          handleEditChange('purchase_order_id', null);
        };
        const switchToDropdown = () => {
          setPoManualMode(false);
          handleEditChange('purchase_order_no', '');
        };
        const onPoSelect = (poId) => {
          handleEditChange('purchase_order_id', poId || null);
          const po = supplierPOs.find((p) => p.id === poId);
          handleEditChange('purchase_order_no', po?.po_number || '');
        };
        return (
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {editMode ? '✏️ Edit Supplier Bill' : 'Supplier Bill Details'}
            </h3>
            <div className="space-y-1">
              {renderStaticField("Bill Number", selectedItem.bill_number)}
              {renderStaticField("Supplier", selectedItem.supplier_name)}
              {renderField("Supplier Bill No", "supplier_bill_no")}
              {renderField("Bill Date", "bill_date")}
              {renderField("Due Date", "due_date")}

              {/* PO Number — editable when in edit mode. */}
              {editMode ? (
                <div className="py-2 border-b border-gray-100">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600 font-medium">PO Number</span>
                    <button
                      type="button"
                      data-testid="bill-po-mode-toggle"
                      onClick={poManualMode ? switchToDropdown : switchToManual}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      {poManualMode ? 'اختيار من القائمة' : 'إدخال يَدَوي'}
                    </button>
                  </div>
                  {poManualMode ? (
                    <input
                      type="text"
                      data-testid="bill-po-manual-input"
                      value={editData.purchase_order_no || ''}
                      onChange={(e) => handleEditChange('purchase_order_no', e.target.value)}
                      placeholder="رقم أمر الشراء (يَدَوي)"
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <select
                      data-testid="bill-po-select"
                      value={editData.purchase_order_id || ''}
                      onChange={(e) => onPoSelect(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">— بدون أمر شراء —</option>
                      {supplierPOs.map((po) => (
                        <option key={po.id} value={po.id}>
                          {po.po_number} — {po.total?.toLocaleString?.() || po.total} SAR
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ) : (
                renderStaticField("PO Number", selectedItem.purchase_order_no)
              )}

              {renderStaticField("Subtotal", `${selectedItem.subtotal?.toLocaleString()} SAR`)}
              {renderStaticField("VAT", `${selectedItem.vat_amount?.toLocaleString()} SAR`)}
              {renderStaticField("Total", `${selectedItem.total?.toLocaleString()} SAR`)}
              {renderField("Amount Paid", "amount_paid")}
              {renderStaticField("Amount Remaining", `${selectedItem.amount_remaining?.toLocaleString()} SAR`)}
              {renderStaticField("Status", selectedItem.status)}
              {renderField("Notes", "notes")}
              {renderStaticField("Created By", selectedItem.created_by_name || selectedItem.user_name)}
            </div>
            {selectedItem.items?.length > 0 && renderEditableItemsTable(selectedItem.items)}
            {renderAttachment(selectedItem.attachment_url, "Bill Attachment")}
          </>
        );
      }

      default:
        return <p>No details available</p>;
    }
  };

  return (
    <div data-testid="approvals-page">
      <div className="page-header">
        <h2 className="page-title">Pending Approvals</h2>
        <div className="flex items-center gap-2 bg-orange-100 text-orange-800 px-4 py-2 rounded-lg">
          <AlertCircle className="w-5 h-5" />
          <span className="font-semibold">{pendingData.total_count} items pending</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-white text-blue-600' : 'bg-red-500 text-white'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {activeTab === 'customers' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Customer Approvals</h3>
            </div>
            {pendingData.customers?.length > 0 ? (
              pendingData.customers.map(customer => renderApprovalCard(
                customer, 'customers', Users,
                customer.name_arabic || customer.name_english,
                customer.name_english,
                customer.created_by_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'suppliers' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Supplier Approvals</h3>
            </div>
            {pendingData.suppliers?.length > 0 ? (
              pendingData.suppliers.map(supplier => renderApprovalCard(
                supplier, 'suppliers', Truck,
                supplier.name_arabic || supplier.name_english,
                supplier.name_english,
                supplier.created_by_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'quotations' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Quotation Approvals</h3>
            </div>
            {pendingData.quotations?.length > 0 ? (
              pendingData.quotations.map(quotation => renderApprovalCard(
                quotation, 'quotations', FileText,
                quotation.quotation_no,
                quotation.subject,
                quotation.user_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'sales_quotations' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Sales Quotation Approvals</h3>
              <p className="text-xs text-gray-500 mt-1">
                Quotes submitted by Sales Reps awaiting final review &amp; approval.
              </p>
            </div>
            {pendingData.sales_quotations?.length > 0 ? (
              pendingData.sales_quotations.map(sq => renderApprovalCard(
                sq, 'sales-quotations', ClipboardList,
                `${sq.quotation_number || sq.id} · ${sq.customer_name || '-'}`,
                `${sq.project_name || '-'} — Total: ${Number(sq.total || 0).toLocaleString()} ${sq.currency || 'SAR'}`,
                sq.created_by_name || sq.submitted_by_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'invoices' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Invoice Approvals</h3>
            </div>
            {pendingData.invoices?.length > 0 ? (
              pendingData.invoices.map(invoice => renderApprovalCard(
                invoice, 'invoices', FileSpreadsheet,
                invoice.invoice_number,
                invoice.client_name,
                invoice.user_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'purchase_orders' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Purchase Order Approvals</h3>
            </div>
            {pendingData.purchase_orders?.length > 0 ? (
              pendingData.purchase_orders.map(po => renderApprovalCard(
                po, 'purchase-orders', ShoppingCart,
                po.po_number,
                po.supplier_name,
                po.user_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'projects' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Project Approvals</h3>
            </div>
            {pendingData.projects?.length > 0 ? (
              pendingData.projects.map(project => renderApprovalCard(
                project, 'projects', FolderKanban,
                project.name,
                project.client_name,
                project.user_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'payments' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Payment Approvals</h3>
            </div>
            {pendingData.payments?.length > 0 ? (
              pendingData.payments.map(payment => renderApprovalCard(
                payment, 'payments', CreditCard,
                payment.payment_no,
                payment.payment_type === 'received' ? `From: ${payment.customer_name || 'N/A'}` : `To: ${payment.supplier_name || 'N/A'}`,
                payment.user_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'employees' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Employee Approvals</h3>
            </div>
            {pendingData.employees?.length > 0 ? (
              pendingData.employees.map(employee => renderApprovalCard(
                employee, 'employees', UserCog,
                employee.full_name,
                employee.position,
                employee.created_by_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'payroll' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Payroll Approvals</h3>
            </div>
            {pendingData.payroll?.length > 0 ? (
              pendingData.payroll.map(payroll => renderApprovalCard(
                payroll, 'payroll', Wallet,
                payroll.payroll_number || 'Payroll',
                `${payroll.employee_name} - ${payroll.net_salary?.toLocaleString()} SAR`,
                payroll.created_by_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'employee_loans' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Loan/Advance Approvals</h3>
            </div>
            {pendingData.employee_loans?.length > 0 ? (
              pendingData.employee_loans.map(loan => renderApprovalCard(
                loan, 'employee-loans', CreditCard,
                loan.loan_number || 'Loan',
                `${loan.employee_name} - ${loan.amount?.toLocaleString()} SAR (${loan.loan_type})`,
                loan.created_by_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'expense_reports' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Expense Report Approvals</h3>
              <p className="text-sm text-gray-600">Approve to create journal entry, archive expenses, and enable PDF download</p>
            </div>
            {pendingData.expense_reports?.length > 0 ? (
              pendingData.expense_reports.map(report => renderApprovalCard(
                report, 'expense-reports', ClipboardList,
                report.report_no || 'Expense Report',
                `${report.total_amount?.toLocaleString()} SAR - ${report.expenses_count} expenses (${report.period_start} to ${report.period_end})`,
                report.created_by_name || report.user_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'inventory' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Inventory Approvals</h3>
              <p className="text-sm text-gray-600">Review and approve new inventory items</p>
            </div>
            {pendingData.inventory?.length > 0 ? (
              pendingData.inventory.map(item => renderApprovalCard(
                item, 'inventory', Package,
                item.item_name,
                `${item.quantity} ${item.unit} × ${item.unit_price?.toLocaleString()} SAR = ${item.total_value?.toLocaleString()} SAR`,
                item.created_by_name
              ))
            ) : renderEmptyState()}
          </>
        )}

        {activeTab === 'supplier_bills' && (
          <>
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">Pending Supplier Bill Approvals</h3>
              <p className="text-sm text-gray-600">Review and approve supplier bills before they appear in the system</p>
            </div>
            {pendingData.supplier_bills?.length > 0 ? (
              pendingData.supplier_bills.map(bill => renderApprovalCard(
                bill, 'supplier-bills', Receipt,
                `${bill.bill_number} - ${bill.supplier_name}`,
                `Total: ${bill.total?.toLocaleString()} SAR | Due: ${bill.due_date}`,
                bill.created_by_name || bill.user_name
              ))
            ) : renderEmptyState()}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">
                {editMode ? 'Edit Details' : 'Review for Approval'}
              </h2>
              <button onClick={closeDetails} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {renderDetailContent()}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-between">
              <div>
                {!editMode ? (
                  <button
                    onClick={() => setEditMode(true)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" /> Edit
                  </button>
                ) : (
                  <button
                    onClick={() => setEditMode(false)}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                {editMode ? (
                  <button
                    onClick={handleSaveEdit}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" /> Save Changes
                  </button>
                ) : (
                  <>
                    <button
                      onClick={closeDetails}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <ActionGatedButton
                      action={selectedType === 'invoices' ? 'invoice.reject' : null}
                      ctx={selectedItem}
                      onClick={() => handleReject(selectedType, selectedItem.id)}
                      data-testid="reject-button"
                      className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-rose-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" /> Reject
                    </ActionGatedButton>
                    <ActionGatedButton
                      action={selectedType === 'invoices' ? 'invoice.approve' : null}
                      ctx={selectedItem}
                      onClick={() => handleApprove(selectedType, selectedItem.id)}
                      data-testid="approve-button"
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-emerald-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" /> Approve
                    </ActionGatedButton>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {filePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[95vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                {filePreview.label}
              </h3>
              <div className="flex items-center gap-2">
                <a
                  href={filePreview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm"
                >
                  <Eye className="w-4 h-4" />
                  Open in New Tab
                </a>
                <a
                  href={filePreview.url}
                  download
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
                <button 
                  onClick={() => setFilePreview(null)} 
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-gray-100">
              {filePreview.isPdf ? (
                <div className="w-full h-[70vh] flex flex-col">
                  <object
                    data={filePreview.url}
                    type="application/pdf"
                    className="w-full h-full rounded border border-gray-300"
                  >
                    <div className="flex flex-col items-center justify-center h-full bg-white rounded p-8 text-center">
                      <FileText className="w-16 h-16 text-red-500 mb-4" />
                      <p className="text-gray-700 mb-4">Unable to display PDF in browser.</p>
                      <div className="flex gap-3">
                        <a
                          href={filePreview.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
                        >
                          <Eye className="w-5 h-5" />
                          Open PDF
                        </a>
                        <a
                          href={filePreview.url}
                          download
                          className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
                        >
                          <Download className="w-5 h-5" />
                          Download PDF
                        </a>
                      </div>
                    </div>
                  </object>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <img 
                    src={filePreview.url} 
                    alt={filePreview.label}
                    className="max-w-full max-h-[70vh] object-contain rounded shadow-lg"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Approvals;
